import React, { useEffect, useState, useMemo } from "react";
import {
  Typography,
  Stack,
  Accordion,
  AccordionDetails,
  Button,
} from "@mui/material";
import { MISSING_CC } from "../Constants/PolicyCodingConstants";
import sortCCValues from "components/common/SharedHelperFunctions";
import CustomAccordionSummary from "styles/customAccordionSummary";
import { ExpandMore } from "@mui/icons-material";
import Chips from "components/Chips";

const FAILED_VALIDATION = "FAILED_VALIDATION";
const VARIANCE = "VARIANCE";
const ALL = "ALL";

const validation = [
  { label: ALL, selected: false, value: ALL },
  { label: "Failed Validation", selected: false, value: FAILED_VALIDATION },
];

const classificationValues = [
  { label: ALL, selected: false, value: ALL },
  { label: "PA Required", selected: false, value: "paReqd" },
  { label: "PA Not Required", selected: false, value: "paNotReqd" },
  { label: "Not Covered", selected: false, value: "notCovered" },
];

const varianceValues = [
  { label: ALL, selected: false, value: ALL },
  { label: "Variance", selected: false, value: VARIANCE },
];

const FiltersComponent = ({
  selectedHealthPlan,
  onAccordionToggle,
  rows,
  setFilteredRows,
  setshowErrors,
  validationFilter,
  setValidationFilter,
}) => {
  const ccValues = useMemo(() => {
    const ccValueObj = { missingCCNumber: false };
    const uniqueCCValues = [];
    rows.forEach((r) => {
      if (!r.ccNumber || !/\d/.test(r.ccNumber)) {
        ccValueObj.missingCCNumber = true;
      } else if (r.ccNumber.includes(",")) {
        r.ccNumber.split(",").forEach((ccNumber) => {
          const trimmedCCNumber = ccNumber.trim();
          if (
            trimmedCCNumber.length &&
            !uniqueCCValues.includes(trimmedCCNumber)
          ) {
            uniqueCCValues.push(trimmedCCNumber);
          }
        });
      } else {
        const trimmedCCNumber = r.ccNumber.trim();
        if (
          trimmedCCNumber.length &&
          !uniqueCCValues.includes(trimmedCCNumber)
        ) {
          uniqueCCValues.push(trimmedCCNumber);
        }
      }
    });
    uniqueCCValues.sort((a, b) => sortCCValues(a, b));
    ccValueObj.uniqueCCValues = uniqueCCValues;
    return ccValueObj;
  }, [rows]);

  const renderCCValues = ccValues.uniqueCCValues.map((v) => ({
    label: v,
    selected: false,
    value: v,
  }));
  const [selectedCC, setSelectedCC] = useState(ALL);
  const [expanded, setExpanded] = useState(false);
  const [selectedClassificiation, setSelectedClassificiation] = useState(ALL);
  const [selectedVariance, setSelectedVariance] = useState(ALL);

  if (ccValues.missingCCNumber) {
    renderCCValues.unshift({
      label: MISSING_CC,
      selected: false,
      value: MISSING_CC,
    });
  }
  renderCCValues.unshift({ label: ALL, selected: false, value: ALL });

  // Show errors toggle (prop setter is fine to call in an effect)
  useEffect(() => {
    setshowErrors(validationFilter === FAILED_VALIDATION);
  }, [validationFilter, setshowErrors]);

  // --- helper: does a row match the selected CC? ---
  const rowMatchesSelectedCC = (r, value) => {
    if (value === ALL) return true;
    if (value === MISSING_CC) {
      return (
        !r.ccNumber ||
        !/\d/.test(r.ccNumber) ||
        r.ccNumber === "," ||
        r.ccNumber.trim().length === 0
      );
    }
    if (!r.ccNumber) return false;
    if (r.ccNumber.includes(",")) {
      return r.ccNumber
        .split(",")
        .map((cc) => cc.trim())
        .includes(value);
    }
    return r.ccNumber.trim() === value;
  };

  // --- derive filtered rows without setting local state inside an effect ---
  const filteredRows = useMemo(() => {
    let out = rows;

    if (validationFilter === FAILED_VALIDATION) {
      out = out.filter((r) => r.errors && Object.keys(r.errors).length);
    }

    if (selectedCC !== ALL) {
      out = out.filter((r) => rowMatchesSelectedCC(r, selectedCC));
    }

    if (selectedClassificiation !== ALL) {
      out = out.filter(
        (r) => r.codingClassification === selectedClassificiation
      );
    }

    if (selectedVariance !== ALL) {
      out = out.filter(
        (r) =>
          selectedHealthPlan !== "AHCS" &&
          (["NEW", "VARIANCE"].includes(r.editRule) ||
            r.ruleVersion?.includes("-"))
      );
    }

    return out;
  }, [
    rows,
    validationFilter,
    selectedCC,
    selectedClassificiation,
    selectedVariance,
    selectedHealthPlan,
  ]);

  // Push derived rows up to parent (allowed; this is syncing with "external" owner state)
  useEffect(() => {
    setFilteredRows(filteredRows);
  }, [filteredRows, setFilteredRows]);

  // IMPORTANT: handle CC selection *outside* of effects.
  // If a CC is chosen that yields no rows, immediately revert to ALL here.
  const handleSelectCC = (data) => {
    const next = data.value;
    if (next === ALL) {
      setSelectedCC(ALL);
      return;
    }
    const hasAny = rows?.some((r) => rowMatchesSelectedCC(r, next));
    setSelectedCC(hasAny ? next : ALL);
  };

  const handleAccordionChange = () => {
    setExpanded((prev) => {
      const newExpanded = !prev;
      onAccordionToggle();
      return newExpanded;
    });
  };

  const handleResetFilters = () => {
    setSelectedCC(ALL);
    setSelectedClassificiation(ALL);
    setValidationFilter(ALL);
    setSelectedVariance(ALL);
  };

  const isResetVisible =
    selectedCC !== ALL ||
    selectedClassificiation !== ALL ||
    selectedVariance !== ALL ||
    validationFilter !== ALL;

  return (
    <Accordion
      sx={{ padding: 0, margin: 0 }}
      expanded={expanded}
      onChange={handleAccordionChange}
    >
      <CustomAccordionSummary
        sx={{ padding: 0, margin: 0 }}
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ padding: 0, mb: 0 }}
          px={(theme) => theme.spacing(1)}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Quick Filters:
          </Typography>
          {isResetVisible && (
            <Button onClick={handleResetFilters} color={"chip"}>
              Reset
            </Button>
          )}
          {!expanded && selectedCC !== ALL && (
            <Typography variant="body2">
              <span style={{ fontWeight: "bold" }}>By Coverage Criteria:</span>{" "}
              {renderCCValues.find((v) => v.value === selectedCC)?.label}
            </Typography>
          )}
          {!expanded && selectedClassificiation !== ALL && (
            <Typography variant="body2">
              <span style={{ fontWeight: "bold" }}>By Classification:</span>{" "}
              {
                classificationValues.find(
                  (v) => v.value === selectedClassificiation
                )?.label
              }
            </Typography>
          )}
          {!expanded && validationFilter !== ALL && (
            <Typography variant="body2">
              <span style={{ fontWeight: "bold" }}>By Validation:</span>{" "}
              {validation.find((v) => v.value === validationFilter)?.label}
            </Typography>
          )}
          {!expanded && selectedVariance !== ALL && (
            <Typography variant="body2">
              <span style={{ fontWeight: "bold" }}>By Variance:</span>{" "}
              {varianceValues.find((v) => v.value === selectedVariance)?.label}
            </Typography>
          )}
        </Stack>
      </CustomAccordionSummary>

      <AccordionDetails sx={{ padding: 0, mb: 1 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
          <Typography
            variant="subtitle1"
            sx={{ minWidth: 170, fontWeight: "bold" }}
          >
            By Coverage Criteria:
          </Typography>
          <Chips
            values={renderCCValues}
            activeFilter={selectedCC}
            selectedFilter={handleSelectCC} // ← changed
          />
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
          <Typography
            variant="subtitle1"
            sx={{ minWidth: 170, fontWeight: "bold" }}
          >
            By Classification:
          </Typography>
          <Chips
            values={classificationValues}
            activeFilter={selectedClassificiation}
            selectedFilter={(data) => setSelectedClassificiation(data.value)}
          />
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
          <Typography
            variant="subtitle1"
            sx={{ minWidth: 170, fontWeight: "bold" }}
          >
            By Validation:
          </Typography>
          <Chips
            values={validation}
            activeFilter={validationFilter}
            selectedFilter={(data) => setValidationFilter(data.value)}
          />
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 0, flexWrap: "wrap" }}>
          <Typography
            variant="subtitle1"
            sx={{ minWidth: 170, fontWeight: "bold" }}
          >
            By Variance:
          </Typography>
          <Chips
            values={varianceValues}
            activeFilter={selectedVariance}
            selectedFilter={(data) => setSelectedVariance(data.value)}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default FiltersComponent;
